import React from 'react';
import { Clock } from 'lucide-react';
import { DAYS, DEFAULT_HOURS } from '../lib/businessHours';
import { Switch } from './ui/switch';
import { Input } from './ui/input';

/**
 * 7-day business hours editor used by both the Vendor self-edit form and the
 * Admin vendor edit form. Controlled component — parent owns the `value` /
 * `onChange` state.
 *
 * Props:
 *  - value: object keyed by day code (see /lib/businessHours)
 *  - onChange(next): called whenever any toggle/time changes
 *  - testidPrefix: optional prefix for data-testid attributes (default 'biz-hours')
 */
const BusinessHoursEditor = ({ value, onChange, testidPrefix = 'biz-hours' }) => {
  // Always render with a fully-populated object — merge defaults so missing
  // days don't blow up the form.
  const hours = { ...DEFAULT_HOURS, ...(value || {}) };

  const setDay = (key, patch) => {
    onChange({ ...hours, [key]: { ...hours[key], ...patch } });
  };
  const applyToAllWeekdays = () => {
    const mon = hours.mon;
    const next = { ...hours };
    ['tue', 'wed', 'thu', 'fri'].forEach((d) => { next[d] = { ...mon }; });
    onChange(next);
  };

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-editor`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-gray-500 flex items-center gap-1">
          <Clock className="w-3.5 h-3.5" /> Set your open/close time per day. Buyers see an &ldquo;Open now&rdquo; badge live on your storefront.
        </div>
        <button
          type="button"
          onClick={applyToAllWeekdays}
          className="text-[11px] text-[#2c54b2] hover:underline"
          data-testid={`${testidPrefix}-copy-weekdays`}
        >
          Copy Mon to Tue-Fri
        </button>
      </div>

      <div className="border border-gray-200 rounded-md divide-y divide-gray-100 overflow-hidden bg-white">
        {DAYS.map((d) => {
          const v = hours[d.key];
          return (
            <div
              key={d.key}
              className="grid grid-cols-[80px_70px_1fr] sm:grid-cols-[110px_90px_1fr] items-center gap-3 px-3 py-2"
              data-testid={`${testidPrefix}-row-${d.key}`}
            >
              <div className="text-sm font-medium text-gray-800">{d.long}</div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={!!v.open}
                  onCheckedChange={(checked) => setDay(d.key, { open: checked })}
                  data-testid={`${testidPrefix}-toggle-${d.key}`}
                />
                <span className={`text-[11px] uppercase tracking-wider ${v.open ? 'text-emerald-600' : 'text-gray-400'}`}>
                  {v.open ? 'Open' : 'Closed'}
                </span>
              </div>
              {v.open ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    className="w-28 sm:w-32"
                    value={v.from || '09:00'}
                    onChange={(e) => setDay(d.key, { from: e.target.value })}
                    data-testid={`${testidPrefix}-from-${d.key}`}
                  />
                  <span className="text-xs text-gray-500">to</span>
                  <Input
                    type="time"
                    className="w-28 sm:w-32"
                    value={v.to || '19:00'}
                    onChange={(e) => setDay(d.key, { to: e.target.value })}
                    data-testid={`${testidPrefix}-to-${d.key}`}
                  />
                </div>
              ) : (
                <div className="text-xs text-gray-400 italic">Closed all day</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BusinessHoursEditor;
